//@flow

import {useEffect, useMemo, useState, useCallback} from 'react';

import {
  getProvidersService,
  getStoreProvidersConfigService,
  getInvoiceTypesService,
  createStoreProvidersConfigService,
  listTaxInvoiceOrdersService,
  generateOrderTaxInvoiceService,
  cancelOrderTaxInvoiceService,
  editOrderTaxInvoiceDataService,
  setTaxInvoiceSettingService,
  getTaxInvoiceSettingService,
} from './promises';

import {actions} from './duck';

import {useStoreInfo} from '../store/utils';
import {useActions} from '../../utils/globalStore';

import {useSelector} from 'react-redux';
import {useStoreCountry} from '../country/utils';
import {TAX_INVOICE_STATE_LABELS} from '../../entities';
import {commaFormat} from '../../utils/number';
import {useStoreCurrency} from '../currency/utils';
import {useAlert} from '../../elements/Alert/AlertContext';
import {TAX_INVOICE_TYPES} from './constants';

import type {
  TaxInvoiceType,
  CreateTaxInvoiceProviderRequest,
  TaxInvoiceDocument,
  PaginationRequest,
  EInvoiceProvider,
  OrderTaxInvoiceDataRequest,
  TaxInvoiceSetting,
  TaxInvoiceStateType,
  OrderTaxInvoiceData,
  TaxInvoiceTypeName,
} from '../../entities';

import type {DefaultTaxInvoiceType} from './constants';

import type {RootState} from '..';
import dayjs from 'dayjs';
import {useIsFeatureInTier} from '../../utils/component';
import {useToastActions} from '../toast-alert/utils';

const listInvoiceActions = {
  setProviders: actions.setProviders,
  setLoading: actions.setFetchingProviders,
};

const listInvoiceSelector = (state: RootState) => ({
  providers: state.taxInvoice.providers,
  loading: state.taxInvoice.isFetchingProviders,
});

export function useTaxInvoiceList(): {
  providers: EInvoiceProvider[],
  loading: boolean,
} {
  const hasTaxInvoiceFeature = useIsFeatureInTier('TAX_INVOICE');
  const {setProviders, setLoading} = useActions(listInvoiceActions);
  const {loading, providers} = useSelector(listInvoiceSelector);

  const storeInfo = useStoreInfo();
  const country = useStoreCountry();

  useEffect(() => {
    if (!hasTaxInvoiceFeature) return;
    if (!storeInfo || !country) return;
    const countryCode = country.code;
    const getProviders = async () => {
      const providers = await getProvidersService(countryCode);
      const storeProviders = await getStoreProvidersConfigService(storeInfo.id);

      const providersWithConfig = providers.map(provider => {
        const config = storeProviders.find(
          storeProvider => storeProvider.taxInvoiceProviderId === provider.id
        );

        return {
          id: provider.id,
          name: provider.name,
          configured: !!config,
          enabled: config ? config.enabled : false,
          storeTaxInvoiceProviderId: config?.id,
          providerConfig: config,
        };
      });

      setProviders(providersWithConfig);
    };
    setLoading(true);
    getProviders().finally(() => setLoading(false));
  }, [country, hasTaxInvoiceFeature, setLoading, setProviders, storeInfo]);

  return {
    providers,
    loading,
  };
}

const taxInvoiceProviderListSelector = (state: RootState) =>
  state.taxInvoice.providers || [];

export function useTaxInvoiceProviders() {
  const providers = useSelector(taxInvoiceProviderListSelector);
  return providers;
}

export function useTaxInvoiceDetail(
  id: string
): {
  invoiceTypes: TaxInvoiceType[],
  provider?: EInvoiceProvider,
} {
  const [invoiceTypes, setInvoiceTypes] = useState<TaxInvoiceType[]>([]);
  const {providers} = useTaxInvoiceList();

  useEffect(() => {
    if (!id) return;
    getInvoiceTypesService(id).then(setInvoiceTypes);
  }, [id]);

  const provider = useMemo(() => {
    const provider = providers.find(provider => provider.id === id);

    return provider;
  }, [providers, id]);

  return {
    invoiceTypes,
    provider,
  };
}

export function useTaxInvoiceActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {showToast} = useToastActions();
  const storeInfo = useStoreInfo();

  useEffect(() => {
    if (!error) return;

    const timeout = setTimeout(() => {
      setError(null);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [error]);

  const createStoreProvider = async (
    payload: CreateTaxInvoiceProviderRequest
  ) => {
    if (!storeInfo) return;
    const storeId = storeInfo.id;
    setLoading(true);
    return createStoreProvidersConfigService(storeId, payload)
      .then(response => {
        return true;
      })
      .catch(error => {
        if (error?.error === 'CurrencyNotSupportedByProviderException') {
          showToast({
            message:
              'La moneda de la tienda no es soportada por el proveedor de facturación',
            title: 'Error de configuración',
            type: 'danger',
          });
          return;
        }
        showToast({
          message: 'Ocurrió un error al guardar la configuración',
          title: 'Error',
          type: 'danger',
        });
        setError(error.message);
        return false;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return {
    createStoreProvider,
    loading,
    error,
  };
}

const tableFilterActions = {
  setQuery: actions.setTableQuery,
  setPagination: actions.setTablePagination,
  setEndDate: actions.setTableEndDate,
  setStartDate: actions.setTableStartDate,
  resetFilters: actions.resetTableFilters,
};
const tableFilterSelector = (state: RootState) => state.taxInvoice.tableFilters;

export function useTaxInvoiceTableFilter() {
  const tableFilters = useSelector(tableFilterSelector);

  return tableFilters;
}

export function useTaxInvoiceTableFilterActions() {
  const {
    setQuery,
    setPagination,
    setEndDate,
    setStartDate,
    resetFilters,
  } = useActions(tableFilterActions);

  const setQueryFilter = useCallback(
    (query: string) => {
      setQuery(query);
      setPagination({size: 10, page: 0, total: 0});
    },
    [setQuery, setPagination]
  );

  const setPaginationFilter = useCallback(
    (pagination: PaginationRequest) => {
      setPagination(pagination);
    },
    [setPagination]
  );

  const setEndDateFilter = useCallback(
    (endDate: string) => {
      setEndDate(endDate);
      setPagination({size: 10, page: 0, total: 0});
    },
    [setEndDate, setPagination]
  );

  const setStartDateFilter = useCallback(
    (startDate: string) => {
      setStartDate(startDate);
      setPagination({size: 10, page: 0, total: 0});
    },
    [setStartDate, setPagination]
  );

  const resetTableFilters = useCallback(() => {
    console.log('reset');
    resetFilters();
  }, [resetFilters]);

  return {
    setQueryFilter,
    setPaginationFilter,
    setEndDateFilter,
    setStartDateFilter,
    resetTableFilters,
  };
}

export function useTaxInvoiceTablePagination() {
  const {pagination} = useTaxInvoiceTableFilter();

  const {setPaginationFilter} = useTaxInvoiceTableFilterActions();
  const totalItems = useSelector(
    (state: RootState) => state.taxInvoice.totalOrders
  );

  const nextPage = useCallback(() => {
    setPaginationFilter({
      ...pagination,
      page: pagination.page + 1,
    });
  }, [pagination, setPaginationFilter]);

  const prevPage = useCallback(() => {
    setPaginationFilter({
      ...pagination,
      page: pagination.page - 1,
    });
  }, [pagination, setPaginationFilter]);

  const setPagination = useCallback(
    (page: number) => {
      setPaginationFilter({
        ...pagination,
        page,
      });
    },
    [pagination, setPaginationFilter]
  );

  return {
    pagination: {
      ...pagination,
      total: totalItems,
    },

    nextPage,
    prevPage,
    setPagination,
  };
}

const orderTaxInvoiceListSelector = (state: RootState) =>
  state.taxInvoice.orderTaxInvoiceList;

export function useTaxInvoiceDocumentOrders(): {
  documents: TaxInvoiceDocument[],
  loading: boolean,
} {
  const documents = useSelector(orderTaxInvoiceListSelector);
  const setOrderTaxInvoiceList = useActions(actions.setOrderTaxInvoiceList);
  const setTotalOrders = useActions(actions.setTotalOrders);
  const tableFilters = useTaxInvoiceTableFilter();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const {pagination, endDate, startDate, query} = tableFilters;
    setLoading(true);
    listTaxInvoiceOrdersService({
      pagination,
      startDate,
      endDate,
      query,
    })
      .then(({data, pagination}) => {
        const orderTaxInvoiceList: TaxInvoiceDocument[] = data.map(order => {
          let canBeCancelled = true;

          if (order.issueDate) {
            const issueDate = dayjs(order.issueDate);
            const issueDateInterval = issueDate.add(2, 'day');
            const today = dayjs();

            canBeCancelled = today.isBefore(issueDateInterval);
          }

          const itemCurrency = order.taxInvoiceProvider.currency;

          return {
            clientName: order.taxReceiverName,
            orderId: order.orderId,
            orderTaxInvoiceId: order.id,
            createdAt: order.createdAt,
            isManualOrder: false,
            orderAmount: commaFormat(order.amount, itemCurrency.symbol),
            documentType: {
              id: order.taxInvoiceType.id,
              label: order.taxInvoiceType.label,
              name: order.taxInvoiceType.name,
              code: '',
            },
            state: {
              id: order.state,
              label: TAX_INVOICE_STATE_LABELS[order.state],
            },
            pdfUrl: order.pdfUrl,
            issueDate: order.issueDate,
            canBeCancelledWithoutCreditNote: canBeCancelled,
            formattedExternalReferenceId: order.formattedExternalReferenceId,
          };
        });

        setOrderTaxInvoiceList(orderTaxInvoiceList);
        setTotalOrders(pagination.total);
      })
      .catch(error => {
        console.log({error});
        setOrderTaxInvoiceList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setOrderTaxInvoiceList, setTotalOrders, tableFilters]);

  return {
    documents: documents || [],
    loading,
  };
}

/* const taxInvoiceTypeSelector = (
  state: RootState
): StoreTaxInvoiceType[] | typeof undefined =>
  state.taxInvoice.storeTaxInvoiceType;
 */
export function useStoreTaxInvoiceType(): {
  storeTaxInvoiceType: DefaultTaxInvoiceType[],
} {
  const taxInvoiceSetting = useTaxInvoiceSetting();

  const storeTaxInvoiceType = useMemo(() => {
    if (!taxInvoiceSetting || !taxInvoiceSetting.enabled) return [];
    return TAX_INVOICE_TYPES.filter(storeTaxInvoiceType => {
      return !!taxInvoiceSetting?.taxInvoiceSetting[
        storeTaxInvoiceType?.name?.toUpperCase()
      ];
    });
  }, [taxInvoiceSetting]);

  return {
    storeTaxInvoiceType,
  };
}

export function useOrderTaxInvoiceOrderActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setOrderTaxInvoiceList = useActions(actions.setOrderTaxInvoiceList);
  const setTotalOrders = useActions(actions.setTotalOrders);
  const tableFilters = useTaxInvoiceTableFilter();
  const currency = useStoreCurrency();

  const revalidateDocuments = () => {
    const {pagination, endDate, startDate} = tableFilters;
    listTaxInvoiceOrdersService({
      pagination,
      startDate,
      endDate,
    })
      .then(({data, pagination}) => {
        const orderTaxInvoiceList: TaxInvoiceDocument[] = data.map(order => {
          let canBeCancelled = true;

          if (order.issueDate) {
            const issueDate = dayjs(order.issueDate);
            const issueDateInterval = issueDate.add(2, 'day');
            const today = dayjs();

            canBeCancelled = today.isBefore(issueDateInterval);
          }

          return {
            clientName: order.taxReceiverName,
            orderId: order.orderId,
            orderTaxInvoiceId: order.id,
            createdAt: order.createdAt,
            isManualOrder: false,
            orderAmount: commaFormat(order.amount, currency && currency.symbol),
            documentType: {
              id: order.taxInvoiceType.id,
              label: order.taxInvoiceType.label,
              name: order.taxInvoiceType.name,
              code: '',
            },
            state: {
              id: order.state,
              label: TAX_INVOICE_STATE_LABELS[order.state],
            },
            pdfUrl: order.pdfUrl,
            canBeCancelledWithoutCreditNote: canBeCancelled,
          };
        });

        setOrderTaxInvoiceList(orderTaxInvoiceList);
        setTotalOrders(pagination.total);
      })
      .catch(error => {
        console.log({error});
        setOrderTaxInvoiceList([]);
      });
  };

  useEffect(() => {
    if (!error) return;

    const timeout = setTimeout(() => {
      setError(null);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [error]);

  const generateOrderTaxInvoice = async (orderId: string) => {
    setLoading(true);
    try {
      const response = await generateOrderTaxInvoiceService(orderId);
      if (!response)
        throw new Error('Ocurrio un error al generar el documento');
      return response;
    } catch (e) {
      setError('Ocurrio un error al generar el documento');
    } finally {
      setLoading(false);
    }
  };

  const cancelOrderTaxInvoice = async (orderId: string) => {
    setLoading(true);
    try {
      await cancelOrderTaxInvoiceService(orderId);
      revalidateDocuments();
    } catch (e) {
      setError('Ocurrio un error al cancelar el documento');
    } finally {
      setLoading(false);
    }
  };

  const editOrderTaxInvoiceData = async (
    orderId: string,
    data: OrderTaxInvoiceDataRequest
  ) => {
    setLoading(true);
    try {
      const response = await editOrderTaxInvoiceDataService(orderId, data);
      return response;
    } catch (error) {
      setError('Error al editar la factura');
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    generateOrderTaxInvoice,
    cancelOrderTaxInvoice,
    editOrderTaxInvoiceData,
  };
}

export function useInitializeTaxInvoiceSetting() {
  const setTaxInvoiceSettingAction = useActions(actions.setTaxInvoiceSetting);
  const store = useStoreInfo();

  useEffect(() => {
    if (!store) return;
    getTaxInvoiceSettingService()
      .then(res => {
        setTaxInvoiceSettingAction(res);
      })
      .catch(error => {
        console.error('Error getting tax invoice setting', error);
      });
  }, [setTaxInvoiceSettingAction, store]);
}

const taxInvoiceSettingSelector = (state: RootState) =>
  state.taxInvoice.taxInvoiceSetting;

export function useTaxInvoiceSetting(): TaxInvoiceSetting | typeof undefined {
  const taxInvoiceSetting = useSelector(taxInvoiceSettingSelector);
  return taxInvoiceSetting;
}
export function useTaxInvoiceSettingActions() {
  const setTaxInvoiceSettingAction = useActions(actions.setTaxInvoiceSetting);
  const [loading, setLoading] = useState(false);
  const {showAlert} = useAlert();

  const setTaxInvoiceSetting = useCallback(
    async (payload: TaxInvoiceSetting) => {
      setLoading(true);
      try {
        await setTaxInvoiceSettingService(payload).then(res => {
          setTaxInvoiceSettingAction(res);
        });
      } catch (error) {
        console.error(`Error setting sale recipe config: ${error}`);
        showAlert('No se pudo actualizar la configuración', {
          type: 'error',
          duration: 3000,
        });
      } finally {
        setLoading(false);
      }
    },
    [setTaxInvoiceSettingAction, showAlert]
  );

  return {
    setTaxInvoiceSetting,
    loading,
  };
}

const DISABLED_CANCEL_STATES: TaxInvoiceStateType[] = [
  'REJECTED',
  'CANCELED',
  'PENDING',
  'FAILED',
];

const unCancelledTaxInvoiceType: TaxInvoiceTypeName[] = ['NOTA_CREDITO'];

export function taxInvoiceCanBeCancelled(
  taxInvoice: OrderTaxInvoiceData,
  creditNoteList: OrderTaxInvoiceData[]
) {
  if (!taxInvoice) return false;
  if (unCancelledTaxInvoiceType.includes(taxInvoice.taxInvoiceType.name)) {
    return false;
  }
  if (DISABLED_CANCEL_STATES.includes(taxInvoice.state)) return false;

  const hasRelatedCreditNote = creditNoteList.some(
    item =>
      item.relatedTaxInvoiceId === taxInvoice.id &&
      (item.state === 'ACCEPTED' ||
        item.state === 'PENDING' ||
        item.state === 'SCHEDULED')
  );

  return !hasRelatedCreditNote;
}

const MAX_DAYS_TO_CANCEL = 4;

export function taxInvoiceCanCancelWithoutCreditNote(
  taxInvoice: OrderTaxInvoiceData
) {
  if (!taxInvoice) return false;
  const {issueDate} = taxInvoice;
  if (!issueDate) return true;

  const issueDateObject = new Date(issueDate);
  const currentDate = new Date();
  const timeDifference = currentDate - issueDateObject;
  const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

  return daysDifference <= MAX_DAYS_TO_CANCEL;
}

export function verifyInvoiceCreation(
  taxInvoiceList: OrderTaxInvoiceData[] = []
) {
  if (taxInvoiceList?.length === 0) return true;

  const someTaxInvoiceScheduled = taxInvoiceList?.some(
    taxInvoice =>
      taxInvoice.state === 'PENDING' || taxInvoice.state === 'SCHEDULED'
  );
  if (someTaxInvoiceScheduled) return false;

  const sortedTaxInvoices = [...taxInvoiceList]?.sort(
    (a, b) => b.updatedAt - a.updatedAt
  );

  const [lastTaxInvoice] = sortedTaxInvoices;

  if (lastTaxInvoice.taxInvoiceType.name === 'NOTA_CREDITO') {
    return lastTaxInvoice.state === 'ACCEPTED';
  }

  const statesForNewTaxInvoice: TaxInvoiceStateType[] = [
    'REJECTED',
    'FAILED',
    'CANCELED',
  ];
  if (
    lastTaxInvoice.taxInvoiceType.name === 'FACTURA' ||
    lastTaxInvoice.taxInvoiceType.name === 'BOLETA'
  ) {
    return statesForNewTaxInvoice.includes(lastTaxInvoice.state);
  }

  return true;
}

const TAX_INVOICE_COUNTRY_LIST = ['PE'];

export function useTaxInvoiceEnabled() {
  const hasTaxInvoiceFeature = useIsFeatureInTier('TAX_INVOICE');
  const storeCountry = useStoreCountry();

  if (!hasTaxInvoiceFeature) return false;

  return TAX_INVOICE_COUNTRY_LIST.includes(storeCountry?.code || '');
}
